import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';

export interface ChemicalFilters {
  search: string;
  chemicalType: string;
  supplier: string;
  hazardLevel: string;
}

interface ChemicalFiltersProps {
  filters: ChemicalFilters;
  onFilterChange: (filters: Partial<ChemicalFilters>) => void;
  onClearFilters: () => void;
}

export const ChemicalFilters = ({ filters, onFilterChange, onClearFilters }: ChemicalFiltersProps) => {
  const [searchInput, setSearchInput] = useState(filters.search);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ search: searchInput });
  };

  const hasActiveFilters = filters.search || filters.chemicalType || filters.supplier || filters.hazardLevel;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Filter Raw Materials</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSearchSubmit} className="space-y-2">
          <Label htmlFor="search">Search</Label>
          <div className="flex gap-2">
            <Input
              id="search"
              placeholder="Search by name, CAS number..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button type="submit" size="icon" variant="outline">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </form>

        <div className="space-y-2">
          <Label htmlFor="chemical-type">Chemical Type</Label>
          <Select 
            value={filters.chemicalType} 
            onValueChange={(value) => onFilterChange({ chemicalType: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="ingredient">Ingredient</SelectItem>
              <SelectItem value="solvent">Solvent</SelectItem>
              <SelectItem value="surfactant">Surfactant</SelectItem>
              <SelectItem value="preservative">Preservative</SelectItem>
              <SelectItem value="fragrance">Fragrance</SelectItem>
              <SelectItem value="colorant">Colorant</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="supplier">Supplier</Label>
          <Input
            id="supplier"
            placeholder="Filter by supplier"
            value={filters.supplier}
            onChange={(e) => onFilterChange({ supplier: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="hazard-level">Hazard Level</Label>
          <Select 
            value={filters.hazardLevel} 
            onValueChange={(value) => onFilterChange({ hazardLevel: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All hazard levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All hazard levels</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button 
            variant="outline" 
            onClick={onClearFilters}
            className="w-full"
          >
            Clear Filters
          </Button>
        )}
      </CardContent>
    </Card>
  );
};